import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// ---- Transparent retry policy ----------------------------------------------
// Cloudflare occasionally emits 520 / 521 / 522 / 524 responses when the origin
// is momentarily slow or restarting. Those are transient and typically clear
// within a few hundred ms — but if we surface them, a borrower sees a scary
// "origin could not parse" page. We retry them silently with exponential
// backoff. Also retries hard network errors (dropped connection / no response).
//
// NOT retried:
//   * 4xx (auth/validation) — always a real error, retrying wastes time
//   * 5xx from our own origin (500/502/503 with JSON body) — the app WANTS to
//     handle those and show its own error state
//   * The user's request timing out via explicit axios `timeout` — respect it
//   * Non-idempotent bodies with side effects that must not double-fire
//     (POST/PATCH/DELETE) EXCEPT when Cloudflare rejected the response — we
//     assume the server never processed the request in those cases because
//     Cloudflare's parse error means the response didn't even complete.
const RETRY_STATUS_CODES = new Set([520, 521, 522, 523, 524, 525, 526, 527]);
const MAX_RETRIES = 3;
const BACKOFF_MS = [400, 800, 1600];

const isRetryableError = (error) => {
  // Network-level failures (no response received)
  if (!error.response) {
    // Skip if the user's own explicit timeout config fired
    if (error.code === "ECONNABORTED" && error.config?.timeout) return false;
    // Skip request cancellation from React StrictMode / unmounts
    if (axios.isCancel(error)) return false;
    return true;
  }
  return RETRY_STATUS_CODES.has(error.response.status);
};

// Idempotent by convention; POST/PATCH/DELETE still retried but only when the
// failure was a Cloudflare 520-family status. In those cases the server never
// completed the response, so re-sending doesn't cause duplicate side effects.
const shouldRetry = (config, error) => {
  const method = (config.method || "get").toLowerCase();
  const attempt = config.__retryCount || 0;
  if (attempt >= MAX_RETRIES) return false;
  if (!isRetryableError(error)) return false;
  const isIdempotent = ["get", "head", "options"].includes(method);
  if (isIdempotent) return true;
  // For mutating verbs only retry when it's clearly a CF-edge failure.
  return !!error.response && RETRY_STATUS_CODES.has(error.response.status);
};

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ac_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    if (!shouldRetry(config, error)) return Promise.reject(error);
    config.__retryCount = (config.__retryCount || 0) + 1;
    const delay = BACKOFF_MS[config.__retryCount - 1] || 1600;
    return new Promise((resolve) => setTimeout(resolve, delay)).then(() => api(config));
  }
);
